import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import GreenButton from '../Components/Buttons/Greenbutton';
import Icon from '../Components/Icons/Icon';
import LocationPickerMap from '../Components/LocationPickerMap/LocationPickerMap';
import { useAuth } from '../context/AuthContext';
import { reverseGeocodeOpenStreetMap, searchOpenStreetMap } from '../lib/openStreetMap';

const defaultKathmanduLocation = {
  lat: '27.7172',
  lon: '85.3240',
  label: 'Kathmandu, Bagmati Province, Nepal',
};

function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signup } = useAuth();
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    password: '',
  });
  const [searchQuery, setSearchQuery] = useState('Kathmandu');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(defaultKathmanduLocation);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleAddressSearch(event) {
    event.preventDefault();

    const query = searchQuery.trim();
    if (!query) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setError('');

    try {
      const results = await searchOpenStreetMap(query);
      setSearchResults(results);

      if (results.length > 0) {
        applySearchResult(results[0]);
      } else {
        setSelectedLocation(null);
      }
    } catch (searchError) {
      setError(searchError.message);
    } finally {
      setIsSearching(false);
    }
  }

  function applySearchResult(result) {
    setSelectedLocation({
      lat: result.lat,
      lon: result.lon,
      label: result.display_name,
    });
    updateField('address', result.display_name);
    setSearchResults([]);
    setSearchQuery(result.display_name);
  }

  async function handleMapPickLocation(nextLocation) {
    setSelectedLocation({
      ...nextLocation,
      label: 'Selected map location',
    });
    setError('');

    try {
      const result = await reverseGeocodeOpenStreetMap(nextLocation.lat, nextLocation.lon);
      const displayName = result.display_name || `Pinned location (${Number(nextLocation.lat).toFixed(5)}, ${Number(nextLocation.lon).toFixed(5)})`;

      setSelectedLocation({
        lat: nextLocation.lat,
        lon: nextLocation.lon,
        label: displayName,
      });
      updateField('address', displayName);
      setSearchQuery(displayName);
      setSearchResults([]);
    } catch (mapError) {
      setError(mapError.message);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await signup({
        ...form,
        locationLabel: selectedLocation?.label || '',
        latitude: selectedLocation?.lat || '',
        longitude: selectedLocation?.lon || '',
      });
      navigate(location.state?.from || (response.user.isAdmin ? '/admin' : '/products'), {
        replace: true,
      });
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="page-card auth-shell signup-shell">
      <div className="signup-shell__header">
        <span className="auth-shell__badge">
          <Icon name="user-check" size={26} />
        </span>
        <h1 className="page-card__title">Create your account</h1>
        <p className="page-card__text">
          Sign up once and save your delivery details for faster checkout.
        </p>
      </div>
      <form className="auth-form signup-form" onSubmit={handleSubmit}>
        <label className="auth-form__field">
          <span>Full Name</span>
          <input
            required
            type="text"
            value={form.fullName}
            onChange={(event) => {
              updateField('fullName', event.target.value);
            }}
          />
        </label>
        <label className="auth-form__field">
          <span>Email</span>
          <input
            required
            type="email"
            value={form.email}
            onChange={(event) => {
              updateField('email', event.target.value);
            }}
          />
        </label>
        <label className="auth-form__field">
          <span>Phone</span>
          <input
            required
            type="tel"
            value={form.phone}
            onChange={(event) => {
              updateField('phone', event.target.value);
            }}
          />
        </label>
        <label className="auth-form__field">
          <span>Password</span>
          <input
            required
            minLength={6}
            type="password"
            value={form.password}
            onChange={(event) => {
              updateField('password', event.target.value);
            }}
          />
        </label>
        <div className="checkout-location signup-form__full">
          <div className="checkout-location__header">
            <div>
              <span className="checkout-location__label">Delivery Location</span>
              <h2 className="checkout-location__title">Set your address on map</h2>
            </div>
          </div>
          <div className="checkout-location__search">
            <input
              type="search"
              placeholder="Search area, street, or landmark"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
              }}
            />
            <button
              type="button"
              className="product-card__link"
              onClick={handleAddressSearch}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
          {searchResults.length ? (
            <div className="checkout-location__results">
              {searchResults.map((result) => (
                <button
                  key={`${result.place_id}`}
                  type="button"
                  className="checkout-location__result"
                  onClick={() => {
                    applySearchResult(result);
                  }}
                >
                  {result.display_name}
                </button>
              ))}
            </div>
          ) : null}
          <div className="checkout-location__map-shell">
            <LocationPickerMap
              selectedLocation={selectedLocation}
              onPickLocation={(nextLocation) => {
                void handleMapPickLocation(nextLocation);
              }}
            />
          </div>
          {selectedLocation ? (
            <p className="checkout-location__selected">
              Selected location: {selectedLocation.label}
              <br />
              Click anywhere on the map to move the pin.
            </p>
          ) : null}
        </div>
        {error ? <p className="products-state products-state--error signup-form__full">{error}</p> : null}
        <div className="signup-form__actions signup-form__full">
          <GreenButton
            disabled={isSubmitting}
            type="submit"
            text={isSubmitting ? 'Creating...' : 'Create Account'}
          />
        </div>
      </form>
      <p className="auth-form__switch">
        Already have an account? <Link to="/login">Login</Link>
      </p>
    </section>
  );
}

export default SignupPage;
